import { useState, type JSX } from 'react'
import type { PublicRender, PublicWidget } from '@shared/publicDesk'
import { deskAssetUrl as assetUrl } from './api'
import { CaptureFrame } from './CaptureFrame'
import { SafeHtml } from './SafeHtml'
import { safeCssColor, safeHref } from './safeUrl'

// Browser-safe renderers, one per render family.
//
// Every one of these is read-only by construction rather than by a disabled
// prop: there is no editor, no input that writes anywhere, and no transport to
// write through. Page and tab changes are local component state, so two people
// looking at the same public desk can be on different pages without either of
// them changing anything for the owner.
//
// Nothing in a projection is trusted for being well-formed. Signal validates
// its shape, but not the markup inside a doc or slide, and anyone with an
// account can publish a desk of their own -- built by hand rather than by the
// desktop -- and send the link to someone else. So markup goes through SafeHtml,
// link URLs through safeHref, colours through safeCssColor, and asset ids are
// encoded so they can only ever name an asset (src/api.ts).

// A PDF from the API, framed. Not <object>: the page's CSP sets object-src
// 'none' (plugins are an attack surface this page has no use for), and the
// browser's PDF viewer works in a frame. The open link stays visible beneath it,
// because the frame shows nothing whenever the API refuses to be framed.
function PdfFrame({ src, title }: { src: string; title: string }): JSX.Element {
  return (
    <div className="h-full flex flex-col">
      <iframe src={src} title={title} className="flex-1 min-h-0 w-full bg-white" />
      <div className="shrink-0 px-2 py-1 border-t border-white/10 text-[10px] text-right">
        <a className="underline text-accent" href={src} target="_blank" rel="noreferrer noopener">
          Open PDF ↗
        </a>
      </div>
    </div>
  )
}

/**
 * Minimal markdown: headings, bold, italic, code, links and lists. Written
 * rather than pulled in as a dependency because the input is already sanitized
 * upstream and the output must not be able to introduce HTML.
 */
function renderMarkdown(src: string): JSX.Element {
  const lines = src.split('\n')
  const out: JSX.Element[] = []
  let list: string[] = []
  const flush = (): void => {
    if (list.length) {
      out.push(
        <ul key={`l${out.length}`} className="list-disc pl-5 my-1 space-y-0.5">
          {list.map((li, i) => (
            <li key={i}>{inline(li)}</li>
          ))}
        </ul>
      )
      list = []
    }
  }
  const inline = (s: string): JSX.Element => {
    // Split on the inline markers, keeping the delimiters so they can be styled.
    const parts = s.split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g)
    return (
      <>
        {parts.map((p, i) => {
          if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>
          if (p.startsWith('`') && p.endsWith('`'))
            return (
              <code key={i} className="px-1 rounded bg-white/10 text-[0.9em]">
                {p.slice(1, -1)}
              </code>
            )
          if (p.startsWith('*') && p.endsWith('*')) return <em key={i}>{p.slice(1, -1)}</em>
          return <span key={i}>{p}</span>
        })}
      </>
    )
  }
  for (const line of lines) {
    const li = line.match(/^\s*[-*]\s+(.*)$/)
    if (li) {
      list.push(li[1])
      continue
    }
    flush()
    const h = line.match(/^(#{1,4})\s+(.*)$/)
    if (h) {
      const level = h[1].length
      const size = ['text-lg', 'text-base', 'text-sm', 'text-xs'][level - 1]
      out.push(
        <div key={out.length} className={`${size} font-semibold mt-2 mb-1`}>
          {inline(h[2])}
        </div>
      )
      continue
    }
    if (line.trim() === '') {
      out.push(<div key={out.length} className="h-2" />)
      continue
    }
    out.push(
      <p key={out.length} className="my-0.5">
        {inline(line)}
      </p>
    )
  }
  flush()
  return <>{out}</>
}

function Placeholder({ reason }: { reason: string }): JSX.Element {
  return (
    <div className="h-full w-full flex items-center justify-center p-4 text-center">
      <div>
        <div className="text-2xl opacity-30 mb-1" aria-hidden="true">
          ⃠
        </div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-stone-400">{reason}</div>
      </div>
    </div>
  )
}

function TableRender({ r }: { r: Extract<PublicRender, { type: 'table' }> }): JSX.Element {
  // Sorting is local and ephemeral: it changes what this viewer sees and
  // nothing else.
  const [sort, setSort] = useState<{ col: number; dir: 1 | -1 } | null>(null)
  const rows = sort
    ? [...r.rows].sort((a, b) => {
        const av = a.cells[sort.col]
        const bv = b.cells[sort.col]
        if (av == null) return 1
        if (bv == null) return -1
        return (av < bv ? -1 : av > bv ? 1 : 0) * sort.dir
      })
    : r.rows
  return (
    <div className="h-full overflow-auto text-[11px]">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 bg-stone-900/95 backdrop-blur">
          <tr>
            {r.columns.map((c, i) => (
              <th
                key={c.id}
                scope="col"
                className="text-left font-medium px-2 py-1 border-b border-white/10 whitespace-nowrap cursor-pointer select-none"
                aria-sort={sort?.col === i ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}
                onClick={() =>
                  setSort((s) => (s?.col === i ? { col: i, dir: s.dir === 1 ? -1 : 1 } : { col: i, dir: 1 }))
                }
              >
                {c.name}
                {sort?.col === i ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="odd:bg-white/[0.02]">
              {row.cells.map((cell, i) => (
                <td key={i} className="px-2 py-1 border-b border-white/5 align-top">
                  {cell === null ? '' : String(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {r.truncated && (
        <div className="px-2 py-1 text-[10px] text-stone-500">
          Showing the first {r.rows.length} rows.
        </div>
      )}
    </div>
  )
}

function SlidesRender({ r }: { r: Extract<PublicRender, { type: 'slides' }> }): JSX.Element {
  const [i, setI] = useState(0)
  if (r.slides.length === 0) return <Placeholder reason="Empty deck" />
  const slide = r.slides[Math.min(i, r.slides.length - 1)]
  return (
    <div className="h-full flex flex-col">
      <SafeHtml className="flex-1 overflow-auto p-3 prose-invert text-xs" html={slide.html} />
      <div className="flex items-center justify-between px-2 py-1 border-t border-white/10 text-[10px]">
        <button
          className="px-2 py-0.5 rounded hover:bg-white/10 disabled:opacity-30"
          onClick={() => setI((v) => Math.max(0, v - 1))}
          disabled={i === 0}
          aria-label="Previous slide"
        >
          ‹
        </button>
        <span className="text-stone-400">
          {Math.min(i, r.slides.length - 1) + 1} / {r.slides.length}
        </span>
        <button
          className="px-2 py-0.5 rounded hover:bg-white/10 disabled:opacity-30"
          onClick={() => setI((v) => Math.min(r.slides.length - 1, v + 1))}
          disabled={i >= r.slides.length - 1}
          aria-label="Next slide"
        >
          ›
        </button>
      </div>
    </div>
  )
}

function DiagramRender({ r }: { r: Extract<PublicRender, { type: 'diagram' }> }): JSX.Element {
  const pad = 20
  const xs = r.nodes.map((n) => n.x)
  const ys = r.nodes.map((n) => n.y)
  const minX = Math.min(0, ...xs) - pad
  const minY = Math.min(0, ...ys) - pad
  const w = Math.max(...xs, 100) - minX + pad + 80
  const h = Math.max(...ys, 100) - minY + pad + 40
  const at = (id: string): { x: number; y: number } | null => {
    const n = r.nodes.find((v) => v.id === id)
    return n ? { x: n.x + 40, y: n.y + 12 } : null
  }
  return (
    <svg className="h-full w-full" viewBox={`${minX} ${minY} ${w} ${h}`} role="img" aria-label="Diagram">
      {r.edges.map((e) => {
        const a = at(e.from)
        const b = at(e.to)
        if (!a || !b) return null
        return (
          <line key={e.id} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="rgba(255,255,255,0.25)" strokeWidth={1.5} />
        )
      })}
      {r.nodes.map((n) => (
        <g key={n.id}>
          <rect x={n.x} y={n.y} width={80} height={24} rx={4} fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.18)" />
          <text x={n.x + 40} y={n.y + 16} textAnchor="middle" fontSize={9} fill="rgba(245,245,244,0.85)">
            {n.label.slice(0, 14)}
          </text>
        </g>
      ))}
    </svg>
  )
}

function ChartRender({ r }: { r: Extract<PublicRender, { type: 'chart' }> }): JSX.Element {
  const points = r.series[0]?.points ?? []
  if (points.length === 0) return <Placeholder reason="No data" />
  const max = Math.max(...points.map((p) => p.value), 1)
  return (
    <div className="h-full w-full flex items-end gap-1 p-3" role="img" aria-label={`${r.chart} chart`}>
      {points.map((p, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
          <div
            className="w-full rounded-t bg-accent/60"
            style={{ height: `${Math.max(2, (p.value / max) * 100)}%` }}
            title={`${p.label}: ${p.value}`}
          />
          <span className="text-[9px] text-stone-500 truncate w-full text-center">{p.label}</span>
        </div>
      ))}
    </div>
  )
}

function TimerRender({ r }: { r: Extract<PublicRender, { type: 'timer' }> }): JSX.Element {
  // Display only, derived from the published state. There is no start, stop or
  // reset: those would be mutations, and a public desk has no mutation path.
  const total = Math.floor(r.valueMs / 1000)
  const mm = String(Math.floor(total / 60)).padStart(2, '0')
  const ss = String(total % 60).padStart(2, '0')
  return (
    <div className="h-full w-full flex flex-col items-center justify-center">
      <div className="text-3xl font-light tabular-nums">{`${mm}:${ss}`}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-1">
        {r.running ? r.mode : 'paused'}
      </div>
    </div>
  )
}

export function WidgetBody({
  widget,
  apiBase,
  token
}: {
  widget: PublicWidget
  apiBase: string
  token: string
}): JSX.Element {
  const r = widget.render
  switch (r.type) {
    case 'text':
      return (
        <div className="h-full overflow-auto p-3 text-xs leading-relaxed whitespace-pre-wrap break-words">
          {r.format === 'markdown' ? renderMarkdown(r.body) : r.body}
        </div>
      )

    case 'doc':
      // Sanitised by the desktop builder, but the server stores the markup
      // without inspecting it, so it is sanitised again here, where it runs.
      return <SafeHtml className="h-full overflow-auto p-3 text-xs leading-relaxed doc-body" html={r.html} />

    case 'table':
      return <TableRender r={r} />

    case 'slides':
      return <SlidesRender r={r} />

    case 'image':
      return r.assetId ? (
        <img
          src={assetUrl(apiBase, token, r.assetId)}
          alt={r.alt}
          className="h-full w-full object-contain"
          draggable={false}
        />
      ) : (
        <Placeholder reason="Image not published" />
      )

    case 'media':
      if (!r.assetId) return <Placeholder reason="Media not published" />
      return r.media === 'audio' ? (
        <div className="h-full flex items-center px-3">
          <audio controls className="w-full" src={assetUrl(apiBase, token, r.assetId)} />
        </div>
      ) : (
        <video controls className="h-full w-full object-contain" src={assetUrl(apiBase, token, r.assetId)} />
      )

    case 'pdf':
      return r.assetId ? (
        <PdfFrame src={assetUrl(apiBase, token, r.assetId)} title={widget.title || 'PDF'} />
      ) : (
        <Placeholder reason="PDF not published" />
      )

    case 'link': {
      // Never a live authenticated view: the public page does not inherit the
      // owner's session, so this is URL chrome plus a safe way out to the site.
      // Signal only accepts http(s) here, but the viewer checks again rather
      // than trust that every stored projection went through that check.
      const href = safeHref(r.url)
      return (
        <div className="h-full flex flex-col">
          <div className="px-2 py-1 border-b border-white/10 text-[10px] text-stone-400 truncate">
            {r.displayUrl}
          </div>
          <div className="flex-1 flex items-center justify-center p-4">
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                className="text-xs underline text-accent hover:text-white"
              >
                Open site ↗
              </a>
            ) : (
              <span className="text-[11px] uppercase tracking-[0.2em] text-stone-500">Link unavailable</span>
            )}
          </div>
        </div>
      )
    }

    case 'section':
      // Sections are containers; their children are positioned over them.
      return <div className="h-full w-full" />

    case 'timer':
      return <TimerRender r={r} />

    case 'chart':
      return <ChartRender r={r} />

    case 'diagram':
      return <DiagramRender r={r} />

    case 'color': {
      // backgroundColor, not the `background` shorthand: the shorthand takes
      // url(), so a "colour" could make every viewer fetch an address.
      const color = safeCssColor(r.value)
      return color ? (
        <div className="h-full w-full" style={{ backgroundColor: color }} />
      ) : (
        <Placeholder reason="Colour unavailable" />
      )
    }

    case 'field':
      return (
        <div className="h-full flex flex-col justify-center px-3">
          <div className="text-[10px] uppercase tracking-[0.16em] text-stone-500">{r.label}</div>
          <div className="text-sm mt-0.5 break-words">{r.value || '—'}</div>
        </div>
      )

    case 'task-link':
      return (
        <div className="h-full flex flex-col justify-center px-3">
          <div className="text-sm">{r.title}</div>
          {r.status && (
            <div className="text-[10px] uppercase tracking-[0.16em] text-stone-500 mt-1">{r.status}</div>
          )}
        </div>
      )

    case 'file': {
      // A file widget is the desktop's unified file object: it decides what to
      // show from the MIME type, and so does this. Fifty-eight published PNGs
      // rendering as fifty-eight identical document icons is not the desk the
      // owner is looking at.
      const mime = r.mime ?? ''
      if (r.assetId && mime.startsWith('image/')) {
        return (
          <img
            src={assetUrl(apiBase, token, r.assetId)}
            alt={r.name}
            title={r.name}
            className="h-full w-full object-contain"
            draggable={false}
          />
        )
      }
      if (r.assetId && mime.startsWith('video/')) {
        return <video controls className="h-full w-full object-contain" src={assetUrl(apiBase, token, r.assetId)} />
      }
      if (r.assetId && mime.startsWith('audio/')) {
        return (
          <div className="h-full flex items-center px-3">
            <audio controls className="w-full" src={assetUrl(apiBase, token, r.assetId)} />
          </div>
        )
      }
      if (r.assetId && mime === 'application/pdf') {
        return <PdfFrame src={assetUrl(apiBase, token, r.assetId)} title={r.name || 'PDF'} />
      }
      return (
        <div className="h-full flex flex-col items-center justify-center gap-1 px-3 text-center">
          <div className="text-2xl opacity-40" aria-hidden="true">
            ⎘
          </div>
          <div className="text-xs truncate w-full">{r.name}</div>
          {r.assetId ? (
            <a
              className="text-[10px] underline text-accent"
              href={assetUrl(apiBase, token, r.assetId)}
              target="_blank"
              rel="noreferrer noopener"
            >
              Open
            </a>
          ) : (
            <div className="text-[10px] text-stone-500">Not published</div>
          )}
        </div>
      )
    }

    case 'capture':
      // The widget's own markup, fetched as an asset and rendered in a shadow
      // root with the app's stylesheet. Read-only by construction: the capture
      // strips scripts, handlers and every control before it leaves the
      // desktop, and the server checks the bytes again on upload.
      return (
        <CaptureFrame
          apiBase={apiBase}
          token={token}
          assetId={r.assetId}
          kind={r.kind}
          title={widget.title}
        />
      )

    case 'placeholder':
      return <Placeholder reason={r.reason} />
  }
}
